// The ONE reaction model: every read path normalizes here, every write reduces here.
import type { Reaction, ReactionChange, ReactionWire, Reactor, ReactorKind } from "$lib/types.js";
import { isMe, type ViewerIdentity } from "$lib/session-scope.js";
import { externalGuestProvider } from "$lib/utils.js";
import { singleEmoji } from "$lib/emoji.js";

export type { Reaction, ReactionChange, ReactionWire, Reactor, ReactorKind };

// ponytail: client mirror of the relay's caps; keep in step with the server lane.
export const MAX_DISTINCT_REACTIONS = 20;
export const MAX_REACTION_EMOJI_LENGTH = 32;

const KINDS: readonly ReactorKind[] = ["user", "cybo", "external"];
const WIRE_ACTIONS = { add: "add", added: "add", remove: "remove", removed: "remove" } as const;

function record(x: unknown): Record<string, unknown> | undefined {
  return typeof x === "object" && x ? (x as Record<string, unknown>) : undefined;
}

function str(x: unknown): string | undefined {
  return typeof x === "string" && x ? x : undefined;
}

export function inferReactorKind(id: string): ReactorKind {
  return externalGuestProvider(id) ? "external" : "user";
}

export function reactorKey(r: Pick<Reactor, "id" | "kind">): string {
  return `${r.kind}:${r.id}`;
}

export function isMyReactor(r: Reactor, viewer: ViewerIdentity): boolean {
  return r.kind === "user" && isMe(r.id, viewer);
}

export function reactedByMe(r: Reaction, viewer: ViewerIdentity): boolean {
  return r.reactors.some((x) => isMyReactor(x, viewer));
}

// Contract reactor `{id, kind, name}` or a legacy raw row `{userId, userName}`.
function toReactor(x: unknown): Reactor | undefined {
  const o = record(x);
  const id = str(o?.id) ?? str(o?.userId);
  if (!o || !id) return undefined;
  const kind = KINDS.find((k) => k === o.kind) ?? inferReactorKind(id);
  const avatarUrl = str(o.avatarUrl);
  return {
    id,
    kind,
    name: str(o.name) ?? str(o.userName) ?? "",
    ...(avatarUrl ? { avatarUrl } : {}),
  };
}

// A contract chip lists its reactors; a legacy raw row IS one reactor.
function chipReactors(o: Record<string, unknown>): unknown[] {
  if (Array.isArray(o.reactors)) return o.reactors;
  return typeof o.count === "number" ? [] : [o];
}

/** Contract chips or legacy raw rows → one chip per emoji, each reactor once. Idempotent. */
export function normalizeReactions(raw: unknown): Reaction[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const chips = new Map<string, Reaction>();
  for (const item of raw) {
    const o = record(item);
    const emoji = str(o?.emoji);
    if (!o || !emoji) continue;
    const chip = chips.get(emoji) ?? { emoji, count: 0, reactors: [] };
    chips.set(emoji, chip);
    for (const r of chipReactors(o).map(toReactor)) {
      if (r && !chip.reactors.some((x) => reactorKey(x) === reactorKey(r))) chip.reactors.push(r);
    }
    if (typeof o.count === "number") chip.count = Math.max(chip.count, o.count);
    chip.count = Math.max(chip.count, chip.reactors.length);
  }
  const out = [...chips.values()].filter((c) => c.count > 0);
  return out.length > 0 ? out : undefined;
}

/** Any live frame → ReactionChange; `resolveLegacy` fills what an old frame lacks. */
export function normalizeReactionEvent(
  wire: ReactionWire,
  resolveLegacy?: (id: string) => Partial<Reactor> | undefined,
): ReactionChange | undefined {
  const emoji = str(wire.emoji);
  if (!emoji || !str(wire.messageId)) return undefined;
  let reactor = toReactor(wire.reactor);
  if (!reactor) {
    // Never `fromId: rp.userId`: the daemon frame has only fromId, and that clobbered it.
    const id = str(wire.userId) ?? str(wire.fromId);
    if (!id) return undefined;
    const known = resolveLegacy?.(id);
    reactor = {
      id: known?.id ?? id,
      kind: known?.kind ?? inferReactorKind(id),
      name: str(wire.userName) ?? str(wire.fromName) ?? known?.name ?? "",
    };
  }
  const action = wire.action ? WIRE_ACTIONS[wire.action] : undefined;
  return {
    workspaceId: wire.workspaceId,
    messageId: wire.messageId,
    emoji,
    action,
    reactor,
    ...(wire.origin ? { origin: wire.origin } : {}),
  };
}

/** Set MY presence to exactly `on` — absolute, idempotent, two-id safe, touches only mine. */
export function setMyReaction(
  reactions: unknown,
  emoji: string,
  on: boolean,
  me: Reactor,
  viewer: ViewerIdentity,
): Reaction[] {
  const list = normalizeReactions(reactions) ?? [];
  const idx = list.findIndex((r) => r.emoji === emoji);
  if (idx < 0) return on ? [...list, { emoji, count: 1, reactors: [me] }] : list;
  const chip = list[idx];
  const mine = chip.reactors.filter((x) => isMyReactor(x, viewer)).length;
  if (mine === (on ? 1 : 0)) return list;
  const others = chip.reactors.filter((x) => !isMyReactor(x, viewer));
  const count = chip.count - mine + (on ? 1 : 0);
  if (count <= 0) return list.filter((_, i) => i !== idx);
  list[idx] = { ...chip, count, reactors: on ? [...others, me] : others };
  return list;
}

/** Apply one live change. Idempotent per (reactor, emoji): a re-delivered frame is a no-op. */
export function applyReactionChange(
  reactions: unknown,
  change: ReactionChange,
  viewer: ViewerIdentity,
): Reaction[] {
  const list = normalizeReactions(reactions) ?? [];
  const { emoji, reactor } = change;
  if (isMyReactor(reactor, viewer)) {
    // A legacy toggle frame of my own says nothing my optimistic patch doesn't already know.
    if (!change.action) return list;
    return setMyReaction(list, emoji, change.action === "add", reactor, viewer);
  }
  const idx = list.findIndex((r) => r.emoji === emoji);
  const key = reactorKey(reactor);
  const present = idx >= 0 && list[idx].reactors.some((x) => reactorKey(x) === key);
  if (change.action === "remove") {
    if (!present) return list;
    const chip = list[idx];
    if (chip.count <= 1) return list.filter((_, i) => i !== idx);
    list[idx] = {
      ...chip,
      count: chip.count - 1,
      reactors: chip.reactors.filter((x) => reactorKey(x) !== key),
    };
    return list;
  }
  if (present) return list;
  if (idx < 0) return [...list, { emoji, count: 1, reactors: [reactor] }];
  const chip = list[idx];
  list[idx] = { ...chip, count: chip.count + 1, reactors: [...chip.reactors, reactor] };
  return list;
}

/** Mirrors the relay's caps so a refused add never flashes. A chip already on the message
 * (even a legacy non-emoji one) can always be toggled; a new one must be a single emoji. */
export function canAddReaction(reactions: unknown, emoji: string): boolean {
  if (!emoji || emoji.length > MAX_REACTION_EMOJI_LENGTH) return false;
  const list = normalizeReactions(reactions) ?? [];
  if (list.some((r) => r.emoji === emoji)) return true;
  return singleEmoji(emoji) === emoji && list.length < MAX_DISTINCT_REACTIONS;
}

function reactorLabel(r: Reactor): string {
  const name = r.name || "Someone";
  if (r.kind === "cybo") return `${name} (agent)`;
  const provider = r.kind === "external" ? externalGuestProvider(r.id) : undefined;
  return provider ? `${name} (${provider[0].toUpperCase()}${provider.slice(1)})` : name;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Ana, Ethan (agent) and you reacted with 👍" — people, then agents, then you last. */
export function reactionSummary(r: Reaction, viewer: ViewerIdentity, max = 10): string {
  const mine = reactedByMe(r, viewer);
  const others = r.reactors.filter((x) => !isMyReactor(x, viewer));
  const ordered = [
    ...others.filter((x) => x.kind !== "cybo"),
    ...others.filter((x) => x.kind === "cybo"),
  ];
  const labels = ordered.slice(0, max).map(reactorLabel);
  const hidden = r.count - (mine ? 1 : 0) - labels.length;
  if (hidden > 0) labels.push(`${hidden} ${hidden === 1 ? "other" : "others"}`);
  if (mine) labels.push("you");
  const who = joinNames(labels) || "Someone";
  return `${who[0].toUpperCase()}${who.slice(1)} reacted with ${r.emoji}`;
}
